import { buildApiUrl } from "@/config/api";

export interface AccountRoleItem {
  id: number;
  name: string;
}

export interface AccountItem {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  roles: AccountRoleItem[];
  reseller?: boolean;
  is_technician?: boolean;
  calendar_color?: string | null;
  vehicle_id?: number | null;
  created_at?: string | null;
  created_by?: { id: number; name: string } | null;
}

export interface AccountsIndexResponse {
  message: string;
  data: AccountItem[];
}

export interface AccountShowResponse {
  message: string;
  data: AccountItem;
}

export interface CreateAccountPayload {
  name: string;
  email: string;
  password: string;
  phone_number?: string | null;
  roles: number[]; // array in API, but only one should be selected client-side
  reseller: boolean;
  is_technician: boolean;
  calendar_color?: string | null;
  vehicle_id?: number | null;
}

export interface UpdateAccountPayload {
  id: number;
  name: string;
  email: string;
  password?: string | null;
  phone_number?: string | null;
  roles?: number[] | null;
  reseller: boolean;
  is_technician: boolean;
  calendar_color?: string | null;
}

const getAccounts = async (token: string): Promise<AccountsIndexResponse> => {
  const response = await fetch(buildApiUrl("/accounts"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to fetch accounts");
  }

  return response.json();
};

const getResellers = async (token: string): Promise<AccountsIndexResponse> => {
  const response = await fetch(buildApiUrl("/resellers"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to fetch resellers");
  }

  return response.json();
};

const getAccount = async (token: string, id: number): Promise<AccountShowResponse> => {
  const response = await fetch(buildApiUrl(`/accounts/${id}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to fetch account");
  }

  return response.json();
};

const createAccount = async (
  token: string,
  payload: CreateAccountPayload,
): Promise<AccountShowResponse> => {
  const response = await fetch(buildApiUrl("/accounts"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to create account");
  }

  return response.json();
};

const updateAccount = async (
  token: string,
  id: number,
  payload: Omit<UpdateAccountPayload, "id">,
): Promise<AccountShowResponse> => {
  const response = await fetch(buildApiUrl(`/accounts/${id}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id, ...payload }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to update account");
  }

  return response.json();
};

const deleteAccount = async (token: string, id: number): Promise<{ message: string }> => {
  const response = await fetch(buildApiUrl(`/accounts/${id}`), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to delete account");
  }

  return response.json();
};

const impersonateUser = async (token: string, userId: number) => {
  const response = await fetch(buildApiUrl(`/impersonate/${userId}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to impersonate user");
  }

  return response.json();
};

const sendPasswordReset = async (token: string, userId: number) => {
  const response = await fetch(buildApiUrl(`/accounts/${userId}/send-password-reset`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Nem sikerült elküldeni a jelszó visszaállító linket");
  return data;
};

const stopImpersonate = async (token: string) => {
  const response = await fetch(buildApiUrl("/stop-impersonate"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to stop impersonation");
  }

  return response.json();
};

export default {
  getAccounts,
  getResellers,
  getAccount,
  createAccount,
  updateAccount,
  deleteAccount,
  impersonateUser,
  stopImpersonate,
  sendPasswordReset,
};


